// src/pages/admin/Wallets.jsx
// git commit: "redesign(Wallets): stat cards, click detail panel, inline top-up, mobile sheet"
import { useEffect, useState, useCallback } from "react";
import adminApi from "../../api/adminApi";
import Currency from "../../components/Currency";
import {
  Search,
  RefreshCw,
  Wallet as WalletIcon,
  TrendingUp,
  Users,
  ArrowUpRight,
  ChevronRight,
  X,
  Plus,
  CheckCircle2,
} from "lucide-react";
import { toast } from "react-toastify";

export default function Wallets() {
  const [wallets, setWallets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [balanceFilter, setBalanceFilter] = useState("all");
  const [selected, setSelected] = useState(null);
  const [topupAmount, setTopupAmount] = useState("");
  const [processing, setProcessing] = useState(false);

  const fetchWallets = useCallback(async () => {
    try {
      setLoading(true);
      const res = await adminApi.get("/wallets");
      setWallets(res.data || []);
    } catch {
      toast.error("Failed to load wallets");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchWallets();
  }, [fetchWallets]);

  // Keep selected wallet in sync after refresh
  useEffect(() => {
    if (selected) {
      const updated = wallets.find((w) => w._id === selected._id);
      if (updated) setSelected(updated);
    }
  }, [wallets]);

  // Stats
  const stats = {
    total: wallets.length,
    totalBalance: wallets.reduce((s, w) => s + (w.balance || 0), 0),
    totalSpent: wallets.reduce((s, w) => s + (w.totalSpent || 0), 0),
    highBalance: wallets.filter((w) => (w.balance || 0) >= 5000).length,
  };

  // Filtered list
  const filtered = wallets.filter((w) => {
    const matchSearch =
      !search.trim() ||
      w.userId?.username?.toLowerCase().includes(search.toLowerCase()) ||
      w.userId?.email?.toLowerCase().includes(search.toLowerCase());
    const bal = w.balance || 0;
    const matchFilter =
      balanceFilter === "all" ||
      (balanceFilter === "low" && bal < 1000) ||
      (balanceFilter === "mid" && bal >= 1000 && bal < 10000) ||
      (balanceFilter === "high" && bal >= 10000);
    return matchSearch && matchFilter;
  });

  const handleTopup = async () => {
    const amount = Number(topupAmount);
    if (!amount || amount <= 0) {
      toast.error("Enter a valid amount");
      return;
    }
    if (
      !window.confirm(
        `Add ₦${amount.toLocaleString()} to ${selected.userId?.username}'s wallet?`
      )
    )
      return;

    setProcessing(true);
    try {
      await adminApi.post("/wallets/topup", {
        userId: selected.userId?._id,
        amount,
      });
      toast.success(`₦${amount.toLocaleString()} added successfully`);
      setTopupAmount("");
      fetchWallets();
    } catch {
      toast.error("Top-up failed");
    } finally {
      setProcessing(false);
    }
  };

  return (
    <div className="space-y-5 h-full">
      {/* ── Stat Cards ───────────────────────────────────── */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        {[
          {
            label: "Total Wallets",
            value: stats.total,
            icon: Users,
            color: "text-blue-600 dark:text-blue-400",
            bg: "bg-blue-50 dark:bg-blue-900/20",
            isCount: true,
          },
          {
            label: "Total Balance",
            value: stats.totalBalance,
            icon: WalletIcon,
            color: "text-purple-600 dark:text-purple-400",
            bg: "bg-purple-50 dark:bg-purple-900/20",
          },
          {
            label: "Total Spent",
            value: stats.totalSpent,
            icon: TrendingUp,
            color: "text-green-600 dark:text-green-400",
            bg: "bg-green-50 dark:bg-green-900/20",
          },
          {
            label: "High Balance (₦5k+)",
            value: stats.highBalance,
            icon: ArrowUpRight,
            color: "text-amber-600 dark:text-amber-400",
            bg: "bg-amber-50 dark:bg-amber-900/20",
            isCount: true,
          },
        ].map(({ label, value, icon: Icon, color, bg, isCount }) => (
          <div
            key={label}
            className="bg-white dark:bg-gray-900 rounded-xl border border-gray-200 dark:border-gray-800 p-4"
          >
            <div
              className={`w-8 h-8 rounded-lg ${bg} flex items-center justify-center mb-3`}
            >
              <Icon size={16} className={color} />
            </div>
            <p className="text-lg font-bold text-gray-900 dark:text-white">
              {isCount ? (
                value.toLocaleString()
              ) : (
                <Currency amount={value} />
              )}
            </p>
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
              {label}
            </p>
          </div>
        ))}
      </div>

      {/* ── Main panel ───────────────────────────────────── */}
      <div className="flex gap-5 min-h-0">
        {/* Left: Wallets list */}
        <div
          className={`flex flex-col bg-white dark:bg-gray-900 rounded-xl border border-gray-200 dark:border-gray-800 overflow-hidden transition-all duration-300 ${
            selected ? "w-full lg:w-3/5" : "w-full"
          }`}
        >
          {/* Toolbar */}
          <div className="flex flex-col sm:flex-row gap-3 p-4 border-b border-gray-100 dark:border-gray-800">
            <div className="relative flex-1">
              <Search
                size={15}
                className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"
              />
              <input
                type="text"
                placeholder="Search username or email..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="w-full pl-9 pr-4 py-2.5 text-sm bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 dark:text-white"
              />
            </div>
            <div className="flex gap-2">
              <select
                value={balanceFilter}
                onChange={(e) => setBalanceFilter(e.target.value)}
                className="px-3 py-2.5 text-sm bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 dark:text-white"
              >
                <option value="all">All</option>
                <option value="low">Low (&lt;₦1k)</option>
                <option value="mid">Mid (₦1k–10k)</option>
                <option value="high">High (₦10k+)</option>
              </select>
              <button
                onClick={fetchWallets}
                disabled={loading}
                className="p-2.5 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg hover:border-purple-400 transition"
              >
                <RefreshCw
                  size={15}
                  className={`text-gray-500 ${loading ? "animate-spin" : ""}`}
                />
              </button>
            </div>
          </div>

          {/* Count row */}
          <div className="px-4 py-2.5 bg-gray-50 dark:bg-gray-800/50 border-b border-gray-100 dark:border-gray-800">
            <p className="text-xs text-gray-500 dark:text-gray-400">
              Showing{" "}
              <span className="font-semibold text-gray-700 dark:text-gray-300">
                {filtered.length}
              </span>{" "}
              of{" "}
              <span className="font-semibold text-gray-700 dark:text-gray-300">
                {wallets.length}
              </span>{" "}
              wallets
            </p>
          </div>

          {/* Content */}
          {loading ? (
            <div className="flex-1 flex items-center justify-center py-20">
              <div className="flex flex-col items-center gap-3">
                <RefreshCw size={24} className="text-purple-500 animate-spin" />
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  Loading wallets...
                </p>
              </div>
            </div>
          ) : filtered.length === 0 ? (
            <div className="flex-1 flex items-center justify-center py-20">
              <div className="text-center">
                <WalletIcon
                  size={36}
                  className="mx-auto text-gray-300 dark:text-gray-600 mb-2"
                />
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  No wallets found
                </p>
              </div>
            </div>
          ) : (
            <>
              {/* Desktop table */}
              <div className="hidden md:block overflow-x-auto flex-1">
                <table className="w-full">
                  <thead className="sticky top-0 bg-white dark:bg-gray-900 border-b border-gray-100 dark:border-gray-800">
                    <tr>
                      {["User", "Balance", "Total Spent", "Last Updated", ""].map(
                        (h) => (
                          <th
                            key={h}
                            className="px-5 py-3 text-left text-xs font-semibold text-gray-400 dark:text-gray-500 uppercase tracking-wider"
                          >
                            {h}
                          </th>
                        )
                      )}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-50 dark:divide-gray-800">
                    {filtered.map((wallet) => {
                      const bal = wallet.balance || 0;
                      const isHigh = bal >= 10000;
                      const isMid = bal >= 1000 && bal < 10000;
                      return (
                        <tr
                          key={wallet._id}
                          onClick={() =>
                            setSelected(
                              selected?._id === wallet._id ? null : wallet
                            )
                          }
                          className={`cursor-pointer transition-all ${
                            selected?._id === wallet._id
                              ? "bg-purple-50 dark:bg-purple-900/10"
                              : "hover:bg-gray-50 dark:hover:bg-gray-800/40"
                          }`}
                        >
                          <td className="px-5 py-3.5">
                            <div className="flex items-center gap-3">
                              <div className="w-8 h-8 rounded-full bg-purple-100 dark:bg-purple-900/30 flex items-center justify-center text-purple-600 dark:text-purple-400 text-xs font-bold flex-shrink-0">
                                {wallet.userId?.username?.[0]?.toUpperCase() ||
                                  "?"}
                              </div>
                              <div>
                                <p className="text-sm font-medium text-gray-900 dark:text-white">
                                  {wallet.userId?.username || "—"}
                                </p>
                                <p className="text-xs text-gray-400 dark:text-gray-500 truncate max-w-[160px]">
                                  {wallet.userId?.email || "—"}
                                </p>
                              </div>
                            </div>
                          </td>
                          <td className="px-5 py-3.5">
                            <span
                              className={`text-sm font-bold ${
                                isHigh
                                  ? "text-green-600 dark:text-green-400"
                                  : isMid
                                  ? "text-blue-600 dark:text-blue-400"
                                  : "text-gray-700 dark:text-gray-300"
                              }`}
                            >
                              <Currency amount={bal} />
                            </span>
                          </td>
                          <td className="px-5 py-3.5 text-sm text-gray-500 dark:text-gray-400">
                            <Currency amount={wallet.totalSpent || 0} />
                          </td>
                          <td className="px-5 py-3.5 text-sm text-gray-400 dark:text-gray-500">
                            {new Date(
                              wallet.updatedAt || wallet.createdAt
                            ).toLocaleDateString("en-NG", {
                              day: "numeric",
                              month: "short",
                              year: "numeric",
                            })}
                          </td>
                          <td className="px-5 py-3.5">
                            <ChevronRight
                              size={16}
                              className={`text-gray-300 transition-transform ${
                                selected?._id === wallet._id
                                  ? "rotate-90 text-purple-500"
                                  : ""
                              }`}
                            />
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              {/* Mobile cards */}
              <div className="md:hidden divide-y divide-gray-100 dark:divide-gray-800 overflow-y-auto">
                {filtered.map((wallet) => (
                  <button
                    key={wallet._id}
                    onClick={() =>
                      setSelected(
                        selected?._id === wallet._id ? null : wallet
                      )
                    }
                    className={`w-full text-left px-4 py-4 flex items-center gap-3 transition ${
                      selected?._id === wallet._id
                        ? "bg-purple-50 dark:bg-purple-900/10"
                        : "hover:bg-gray-50 dark:hover:bg-gray-800/40"
                    }`}
                  >
                    <div className="w-10 h-10 rounded-full bg-purple-100 dark:bg-purple-900/30 flex items-center justify-center text-purple-600 dark:text-purple-400 font-bold flex-shrink-0">
                      {wallet.userId?.username?.[0]?.toUpperCase() || "?"}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-gray-900 dark:text-white truncate">
                        {wallet.userId?.username || "—"}
                      </p>
                      <p className="text-xs text-gray-400 dark:text-gray-500 truncate">
                        {wallet.userId?.email || "—"}
                      </p>
                      <p className="text-xs font-bold text-purple-600 dark:text-purple-400 mt-0.5">
                        <Currency amount={wallet.balance || 0} />
                      </p>
                    </div>
                    <ChevronRight size={16} className="text-gray-300 flex-shrink-0" />
                  </button>
                ))}
              </div>
            </>
          )}
        </div>

        {/* Right: Detail panel — desktop */}
        {selected && (
          <div className="hidden lg:flex flex-col w-2/5 bg-white dark:bg-gray-900 rounded-xl border border-gray-200 dark:border-gray-800 overflow-hidden">
            {/* Panel header */}
            <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100 dark:border-gray-800">
              <h3 className="text-sm font-semibold text-gray-900 dark:text-white">
                Wallet Details
              </h3>
              <button
                onClick={() => setSelected(null)}
                className="p-1.5 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 transition"
              >
                <X size={16} className="text-gray-500" />
              </button>
            </div>

            <div className="flex-1 overflow-y-auto p-5 space-y-5">
              {/* Avatar + user info */}
              <div className="flex items-center gap-4">
                <div className="w-14 h-14 rounded-xl bg-purple-100 dark:bg-purple-900/30 flex items-center justify-center text-purple-600 dark:text-purple-400 text-2xl font-bold">
                  {selected.userId?.username?.[0]?.toUpperCase() || "?"}
                </div>
                <div>
                  <p className="text-base font-bold text-gray-900 dark:text-white">
                    {selected.userId?.username || "Unknown"}
                  </p>
                  <p className="text-sm text-gray-500 dark:text-gray-400">
                    {selected.userId?.email || "—"}
                  </p>
                </div>
              </div>

              {/* Wallet metrics */}
              <div className="grid grid-cols-2 gap-3">
                {[
                  {
                    label: "Current Balance",
                    value: <Currency amount={selected.balance || 0} />,
                    icon: WalletIcon,
                    highlight: true,
                  },
                  {
                    label: "Total Spent",
                    value: <Currency amount={selected.totalSpent || 0} />,
                    icon: TrendingUp,
                  },
                  {
                    label: "Last Updated",
                    value: new Date(
                      selected.updatedAt || selected.createdAt
                    ).toLocaleDateString("en-NG", {
                      day: "numeric",
                      month: "short",
                      year: "numeric",
                    }),
                    icon: RefreshCw,
                  },
                  {
                    label: "Virtual Account",
                    value: selected.virtualAccount?.account_number
                      ? selected.virtualAccount.account_number
                      : "Not set",
                    icon: CheckCircle2,
                  },
                ].map(({ label, value, icon: Icon, highlight }) => (
                  <div
                    key={label}
                    className={`p-3 rounded-xl border ${
                      highlight
                        ? "border-purple-200 dark:border-purple-800 bg-purple-50 dark:bg-purple-900/10"
                        : "border-gray-100 dark:border-gray-800 bg-gray-50 dark:bg-gray-800/50"
                    }`}
                  >
                    <div className="flex items-center gap-1.5 mb-1.5">
                      <Icon
                        size={12}
                        className={
                          highlight ? "text-purple-500" : "text-gray-400"
                        }
                      />
                      <span className="text-xs text-gray-400 dark:text-gray-500">
                        {label}
                      </span>
                    </div>
                    <p
                      className={`text-sm font-semibold truncate ${
                        highlight
                          ? "text-purple-700 dark:text-purple-300"
                          : "text-gray-900 dark:text-white"
                      }`}
                    >
                      {value}
                    </p>
                  </div>
                ))}
              </div>

              {/* Virtual account details if exists */}
              {selected.virtualAccount?.account_number && (
                <div className="bg-green-50 dark:bg-green-900/10 border border-green-200 dark:border-green-800 rounded-xl p-4">
                  <p className="text-xs font-semibold text-green-700 dark:text-green-400 mb-2 flex items-center gap-1.5">
                    <CheckCircle2 size={12} />
                    Dedicated Account Active
                  </p>
                  <div className="space-y-1">
                    <div className="flex justify-between text-xs">
                      <span className="text-gray-500 dark:text-gray-400">
                        Bank
                      </span>
                      <span className="font-medium text-gray-900 dark:text-white">
                        {selected.virtualAccount.bank_name || "—"}
                      </span>
                    </div>
                    <div className="flex justify-between text-xs">
                      <span className="text-gray-500 dark:text-gray-400">
                        Account No.
                      </span>
                      <span className="font-mono font-bold text-gray-900 dark:text-white">
                        {selected.virtualAccount.account_number}
                      </span>
                    </div>
                    <div className="flex justify-between text-xs">
                      <span className="text-gray-500 dark:text-gray-400">
                        Account Name
                      </span>
                      <span className="font-medium text-gray-900 dark:text-white truncate max-w-[120px]">
                        {selected.virtualAccount.account_name || "—"}
                      </span>
                    </div>
                  </div>
                </div>
              )}

              {/* Top-up section */}
              <div className="border border-gray-200 dark:border-gray-700 rounded-xl p-4">
                <p className="text-xs font-semibold text-gray-700 dark:text-gray-300 mb-3 flex items-center gap-1.5">
                  <Plus size={13} className="text-purple-500" />
                  Add Funds to Wallet
                </p>
                <div className="flex gap-2">
                  <input
                    type="number"
                    min="1"
                    placeholder="Enter amount (₦)"
                    value={topupAmount}
                    onChange={(e) => setTopupAmount(e.target.value)}
                    className="flex-1 px-3 py-2.5 text-sm bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 dark:text-white"
                  />
                  <button
                    onClick={handleTopup}
                    disabled={processing || !topupAmount}
                    className="px-4 py-2.5 text-sm font-medium bg-purple-600 hover:bg-purple-700 disabled:bg-purple-300 text-white rounded-lg transition flex items-center gap-1.5"
                  >
                    {processing ? (
                      <RefreshCw size={13} className="animate-spin" />
                    ) : (
                      <Plus size={13} />
                    )}
                    {processing ? "..." : "Add"}
                  </button>
                </div>

                {/* Quick amount buttons */}
                <div className="flex gap-2 mt-2">
                  {[500, 1000, 5000, 10000].map((amt) => (
                    <button
                      key={amt}
                      onClick={() => setTopupAmount(String(amt))}
                      className="flex-1 py-1.5 text-xs font-medium text-purple-600 dark:text-purple-400 bg-purple-50 dark:bg-purple-900/20 hover:bg-purple-100 dark:hover:bg-purple-900/30 rounded-lg transition"
                    >
                      ₦{amt >= 1000 ? `${amt / 1000}k` : amt}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Mobile detail sheet */}
      {selected && (
        <div className="lg:hidden fixed inset-0 z-50 flex items-end">
          <div
            className="absolute inset-0 bg-black/50 backdrop-blur-sm"
            onClick={() => setSelected(null)}
          />
          <div className="relative w-full bg-white dark:bg-gray-900 rounded-t-2xl max-h-[85vh] overflow-y-auto">
            <div className="flex justify-center pt-3 pb-1">
              <div className="w-10 h-1 rounded-full bg-gray-300 dark:bg-gray-600" />
            </div>

            <div className="px-5 pb-8 pt-3 space-y-5">
              {/* Header */}
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-11 h-11 rounded-xl bg-purple-100 dark:bg-purple-900/30 flex items-center justify-center text-purple-600 dark:text-purple-400 text-lg font-bold">
                    {selected.userId?.username?.[0]?.toUpperCase() || "?"}
                  </div>
                  <div>
                    <p className="text-sm font-bold text-gray-900 dark:text-white">
                      {selected.userId?.username || "Unknown"}
                    </p>
                    <p className="text-xs text-gray-400 dark:text-gray-500">
                      {selected.userId?.email || "—"}
                    </p>
                  </div>
                </div>
                <button
                  onClick={() => setSelected(null)}
                  className="p-2 rounded-xl hover:bg-gray-100 dark:hover:bg-gray-800"
                >
                  <X size={18} className="text-gray-500" />
                </button>
              </div>

              {/* Balance cards */}
              <div className="grid grid-cols-2 gap-3">
                <div className="bg-purple-50 dark:bg-purple-900/10 border border-purple-200 dark:border-purple-800 rounded-xl p-3">
                  <p className="text-xs text-purple-500 mb-1">Balance</p>
                  <p className="text-sm font-bold text-purple-700 dark:text-purple-300">
                    <Currency amount={selected.balance || 0} />
                  </p>
                </div>
                <div className="bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl p-3">
                  <p className="text-xs text-gray-400 mb-1">Total Spent</p>
                  <p className="text-sm font-bold text-gray-900 dark:text-white">
                    <Currency amount={selected.totalSpent || 0} />
                  </p>
                </div>
              </div>

              {/* Virtual account */}
              {selected.virtualAccount?.account_number && (
                <div className="bg-green-50 dark:bg-green-900/10 border border-green-200 dark:border-green-800 rounded-xl p-4">
                  <p className="text-xs font-semibold text-green-700 dark:text-green-400 mb-2">
                    Dedicated Account
                  </p>
                  <p className="text-base font-mono font-bold text-gray-900 dark:text-white">
                    {selected.virtualAccount.account_number}
                  </p>
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                    {selected.virtualAccount.bank_name} •{" "}
                    {selected.virtualAccount.account_name}
                  </p>
                </div>
              )}

              {/* Top-up */}
              <div className="border border-gray-200 dark:border-gray-700 rounded-xl p-4">
                <p className="text-xs font-semibold text-gray-700 dark:text-gray-300 mb-3">
                  Add Funds
                </p>
                <div className="flex gap-2 mb-2">
                  <input
                    type="number"
                    min="1"
                    placeholder="Enter amount (₦)"
                    value={topupAmount}
                    onChange={(e) => setTopupAmount(e.target.value)}
                    className="flex-1 px-3 py-2.5 text-sm bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 dark:text-white"
                  />
                  <button
                    onClick={handleTopup}
                    disabled={processing || !topupAmount}
                    className="px-4 py-2.5 text-sm font-medium bg-purple-600 hover:bg-purple-700 disabled:bg-purple-300 text-white rounded-lg transition"
                  >
                    {processing ? "..." : "Add"}
                  </button>
                </div>
                <div className="flex gap-2">
                  {[500, 1000, 5000, 10000].map((amt) => (
                    <button
                      key={amt}
                      onClick={() => setTopupAmount(String(amt))}
                      className="flex-1 py-1.5 text-xs font-medium text-purple-600 dark:text-purple-400 bg-purple-50 dark:bg-purple-900/20 rounded-lg transition"
                    >
                      ₦{amt >= 1000 ? `${amt / 1000}k` : amt}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
